import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useContext } from "react";
import Select, { components } from "react-select";
import classes from "../ServicesTable.module.scss"
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { E2Data } from "../../../lib/ddd/ddd";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Workflow from "./Workflow";
import GenericModal from "../../UI/GenericModal";
import { GenericTooltip } from "../../integrationWizard/form/GenericTooltip";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { OrgIdContext } from "../../../store/orgId_context";

const EU_Units = new E2Data("EU_Operators");
EU_Units.format = "full";
const apiRequestAddress = process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";


const FiscOperatorDropdown = forwardRef(({orgData, deviceData, stepData, fieldName="FiscOperatorRegCodeDef", instructions, formData}, ref) => {
    const { integrationDevice, aboutOrgId, sessionInfo} = useContext(OrgIdContext);
    const [e2Data_EU_operators, setE2Data_EU_Operators] = useState(E2Data.WithConnectionKey(apiRequestAddress, integrationDevice, "EU_Operators").ApplyParams({filter:'OrgId="'+aboutOrgId+'"', format:'full'}));
    const [value, setValue] = useState(null);
    const [inputSelected, setInputSelected] = useState();
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [activeWf, setActiveWf] = useState();
    const [autorunWf, setAutorunWf] = useState();
    const [windowWf, setWindowWf] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const wfref = useRef(null);
    const {t} = useTranslation();

    const {isPending, data:unitData, refetch} = useQuery({
        queryKey: ['Data.GetEntityData', e2Data_EU_operators.GetParamsObject()],
        queryFn: () => e2Data_EU_operators.GetData()
    })

    const handleItemSelect = (item) => {
        setValue("");
        setInputSelected(item);
        setIsDropdownVisible(false);
    };

    const onInputChange = (value) => {
        setInputSelected(null);
        setValue(value)
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
          if (
            dropdownRef.current && !dropdownRef.current.contains(e.target) &&
            buttonRef.current && !buttonRef.current.contains(e.target)
          ) {
            setIsDropdownVisible(false);
          }
        };
        document.addEventListener("click", handleClickOutside);
        return () => {
          document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setValue(deviceData?.[fieldName]||null);
        if(instructions?.FiscOperatorRegCode && deviceData?.["FiscOperatorRegCode"]){
            setInputSelected(getOrgUnitFromData(deviceData));
        }else{
            if(Array.isArray(instructions?.workflows)){
                setActiveWf({ wf: instructions?.workflows});
            }
        }
    },[deviceData, instructions]);

    useEffect(() => {
    }, [orgData]);

    useEffect(() => {
    }, [stepData]);

    useEffect(() => {
        
    }, [instructions]);

    const getOrgUnitFromData = (data) => {
        return {
            FiscOperatorRegCode: data?.FiscOperatorRegCode,
            Name: data?.FiscOperatorRegCode__Name,
            OrgId: data?.FiscOperatorRegCode__OrgId,
        }
    }

    const workflowHasFields = (wf) => {
        if(!wf || !Array.isArray(wf)) return false;
        return wf.find(wf => wf?.fields);
    }

    const onCreateNewClick = (e) => {
        e.preventDefault();
        if(!instructions?.FiscOperatorRegCode?.createWorkflows) return;

        if(!workflowHasFields(instructions?.FiscOperatorRegCode?.createWorkflows)){
            const autoRunComplete = (result, error) => {
                setLoading(false);
                setAutorunWf(null);
                _setError(error);
                refetch();
            };
            setAutorunWf({ wf: instructions?.FiscOperatorRegCode?.createWorkflows, onComplete: autoRunComplete, autoExecute: true, formData : formData});
            setLoading(true);
        }else{
            const handleComplete = (result, error) => {
                setWindowWf(null);
                _setError(error);
                refetch();
            };
            setWindowWf({ wf: instructions?.FiscOperatorRegCode?.createWorkflows, onComplete: handleComplete, autoExecute: false, formData : formData});
        }
    }

    const onBeforeSave = async (newData) => {
        let _instructions = instructions;
        if(inputSelected) _instructions = instructions?.FiscOperatorRegCode;
        if(!_instructions) return true;

        if(!_instructions?.workflows?.beforeSave) return true;
        let wf = null;
        if(isCreating()){
            wf = _instructions?.workflows?.beforeSave?.create;
        }else if(isModifying()){
            wf = _instructions?.workflows?.beforeSave?.modify;
        }else if(isDeleting()){
            wf = _instructions?.workflows?.beforeSave?.delete;
        }

        if(!wf) return true;

        return new Promise((resolve) => {
            const handleComplete = (result) => {
                setActiveWf(null);
                resolve(result);
            };
            setActiveWf({ wf, onComplete: handleComplete, autoExecute: true, formData : newData });
        });
    }
    const onAfterSave = async (newData) => {
        let _instructions = instructions;
        if(inputSelected) _instructions = instructions?.FiscOperatorRegCode;
        if(!_instructions) return true;
        if(inputSelected && Array.isArray(_instructions?.workflows)) return await onAfterSaveInputSelectedManual(newData, _instructions?.workflows);
        if(!inputSelected && Array.isArray(instructions?.workflows)) return await onAfterSaveManual(newData, _instructions?.workflows);

        if(!_instructions?.workflows?.afterSave && !Array.isArray(_instructions?.workflows)) return true;
        let wf = null;
        if(isCreating()){
            wf = _instructions?.workflows?.beforeSave?.create;
        }else if(isModifying()){
            wf = _instructions?.workflows?.beforeSave?.modify;
        }else if(isDeleting()){
            wf = _instructions?.workflows?.beforeSave?.delete;
        }
        if(!wf) wf = _instructions?.workflows;

        if(!wf) return true;

        return new Promise((resolve) => {
            const handleComplete = (result) => {
                setActiveWf(null);
                resolve(result);
            };
            setActiveWf({ wf: wf, onComplete: handleComplete, autoExecute: true, formData : newData });
        });
    }

    const onAfterSaveInputSelectedManual = async (newData, wf) => {
        console.log("firing");
        return new Promise((resolve) => {
            const handleComplete = (result) => {
                setAutorunWf(null);
                resolve(result);
            };
            setAutorunWf({  wf: wf, onComplete: handleComplete, autoExecute: true, formData : newData });
        });
    }

    const onAfterSaveManual = async (newData, wf) => {
        if(wfref?.current)
            return wfref?.current?.executeWorkflows(newData);
        return new Promise((resolve) => {
            const handleComplete = (result) => {
                setAutorunWf(null);
                resolve(result);
            };
            setAutorunWf({ wf: wf, onComplete: handleComplete, autoExecute: true, formData : newData });
        });
    }

    const isCreating = () => {
        if(!isEmpty(value) && isEmpty(deviceData?.[fieldName])) return true;
        return false;
    }
    const isModifying = () => {
        if(isEmpty(value) && isEmpty(deviceData?.[fieldName])) return false;
        return isDifferent();
    }
    const isDeleting = () => {
        if(isEmpty(value) && !isEmpty(deviceData?.[fieldName])) return true;
        return false;
    }

    const isEmpty = (str) => {
        return (!str || str?.trim().length === 0);
    }

    const isDifferent = () => {
        return value !== deviceData?.[fieldName];
    }

    const mainWf = () => {
        if(!inputSelected) return true;
        return false;
    }

    const _setError = (error) => {
        if(!error) return;
        setError(error?.message || error?.Reason || t("Unknown error occured"));
        setTimeout(() => {
            setError(null);
        }, 5000);
    }

    useImperativeHandle(ref, () => ({
        onBeforeSave,
        onAfterSave
    }), [value, deviceData, orgData, stepData, instructions, formData, inputSelected, wfref]);

    return (
        <div key={fieldName} className={classes["input-container2"]}>
            <label htmlFor={fieldName}>
                {instructions?.label || fieldName}
                {instructions?.instructionsText && <GenericTooltip tooltip={instructions?.instructionsText} fieldName={fieldName} placement="left"/>}
                {/* {instructions?.instructions && <OverlayTrigger overlay={<Tooltip>{instructions?.instructions}</Tooltip>}>
                    <OpenInNewIcon className={`ms-1 ${classes["hyperlink-icon"]}`} style={{height:"0.9rem", width:"0.9rem"}}/>
                </OverlayTrigger>} */}
                {instructions?.instructions && (() => {
                    const isUrl = /^https?:\/\//i.test(instructions?.instructions); // simple URL check
                    return (
                        <OverlayTrigger overlay={<Tooltip>{instructions?.instructions}</Tooltip>}>
                        <OpenInNewIcon
                            className={`ms-1 ${classes["hyperlink-icon"]}`}
                            style={{ height: "0.9rem", width: "0.9rem", cursor: isUrl ? "pointer" : "default" }}
                            onClick={isUrl ? () => window.open(instructions?.instructions, "_blank") : undefined}
                        />
                        </OverlayTrigger>
                    );
                })()}
            </label>
        <div className={`${classes["dropdown-container"]}`} >
            <input
                type="text"
                value={value}
                onChange={(e) => onInputChange(e.target.value)}
                placeholder={inputSelected ? t("Selected")+`: ${inputSelected?.Name||""} (${inputSelected?.FiscOperatorRegCode||""})` : t("Type or select an option...")}
                className={inputSelected ? `${classes["dropdown-text-field-selected"]}` : `${classes["dropdown-text-field"]}`}
                style={instructions?.FiscOperatorRegCode ? {borderRadius: "8px 0px 0px 8px"} : {borderRadius: "8px"}}
            />

            {instructions?.FiscOperatorRegCode && <button ref={buttonRef} onClick={(e) => {e.preventDefault();setIsDropdownVisible((prev) => !prev)}} className={`${classes["dropdown-button"]}`}>
                <ArrowDropDownIcon className="mb-5"/>
            </button>}

            {isDropdownVisible && (
                <div className={`${classes["dropdown"]}`} ref={dropdownRef}>
                    {instructions?.FiscOperatorRegCode?.createWorkflows && <div key={-1} className={`${classes["dropdown-item"]}`}  onClick={onCreateNewClick}>
                        {t("Create new operator")}
                    </div>}
                {unitData?.result?.data?.map((item, index) => (
                    <div key={index} className={`${classes["dropdown-item"]}`}  onClick={() => handleItemSelect(item)}>
                        {item?.Name} {item?.LastName} ({item?.FiscOperatorRegCode})
                    </div>
                ))}
                </div>
            )}
            {activeWf && mainWf() && <Workflow ref={wfref} orgData={orgData} deviceData={deviceData} formData={activeWf.formData} stepData={stepData} workflow={activeWf?.wf} onComplete={activeWf?.onComplete} value={inputSelected ? inputSelected : value} autoExecute={activeWf?.autoExecute}/>}
            {autorunWf && <><Workflow ref={wfref} orgData={orgData} deviceData={deviceData} formData={autorunWf.formData} stepData={stepData} workflow={autorunWf?.wf} onComplete={autorunWf?.onComplete} value={inputSelected ? inputSelected : value} autoExecute={autorunWf?.autoExecute}/></>}
            <GenericModal show={!!windowWf} onHide={() => setWindowWf(null)} title={t("Enter data")} size="md">
                {windowWf && <Workflow ref={wfref} orgData={orgData} deviceData={deviceData} formData={windowWf.formData} stepData={stepData} workflow={windowWf?.wf} onComplete={windowWf?.onComplete} value={inputSelected ? inputSelected : value} autoExecute={windowWf?.autoExecute}/>}
            </GenericModal>
            
        </div>
        {error && <div className="alert alert-danger mt-2 w-100">{error}</div>}
        </div>
  );
});

export default FiscOperatorDropdown;